import { Component } from '../Component.js';
import { SignaturePad } from '../SignaturePad.js';
import { ROUNDS, BADGES } from '../../config/content.js';
import { storage } from '../../services/storage.js';
import { fk, escHtml, licenceTier, scoredProgress, teamTotal, teamLabel } from '../../utils/helpers.js';

/** localStorage key for the in-progress round draft (answers + signatures).
 *  iPad Safari reloads suspended tabs under memory pressure — anything held
 *  only in JS memory is lost mid-round, so drafts must survive a reload. */
const DRAFT_KEY = 'mv_draft';

export class TeamView extends Component {
  /** @type {Record<string, SignaturePad>} keyed by `${round}_${memberIndex}` */
  #pads = {};
  #savedFieldValues = {};
  #savedSigs = {};
  #currentRound = null;
  #draftTimer = null;

  /**
   * @param {HTMLElement} el
   * @param {{ gameStore: import('../../state/Store.js').Store, localStore: import('../../state/Store.js').Store, firebase: object }} deps
   */
  constructor(el, { gameStore, localStore, firebase }) {
    super(el, {});
    this.gameStore  = gameStore;
    this.localStore = localStore;
    this.firebase   = firebase;
  }

  mount() {
    const G = this.gameStore.getState();
    if (G.round !== this.#currentRound) {
      // Round changed (or first mount after a page load) — restore the stored
      // draft if it belongs to this team + round, otherwise start fresh
      this.#loadDraft(G);
      this.#currentRound = G.round;
    } else {
      // Same round re-render (e.g. bomb drop) — snapshot DOM before wiping
      this.$$('textarea').forEach(ta => {
        if (ta.name && ta.value) this.#savedFieldValues[ta.name] = ta.value;
      });
      this.localStore.getState().members.forEach((_, i) => {
        const pad = this.#pads[`${G.round}_${i}`];
        if (pad?.hasSigned) this.#savedSigs[i] = pad.dataURL;
      });
      this.#persistDraftSoon();
    }
    this.#destroyPads();
    super.mount();
    return this;
  }

  /** Non-destructive: a mismatched draft is left in storage, not deleted —
   *  the first mount after a reload runs with the initial round (-1) before
   *  the Firebase snapshot arrives, and deleting here would wipe the draft
   *  moments before the real round number comes in and restores it. Stale
   *  drafts are simply never loaded, and get overwritten on the next input. */
  #loadDraft(G) {
    const L = this.localStore.getState();
    const d = storage.get(DRAFT_KEY);
    if (d && d.team === L.team && d.round === G.round) {
      this.#savedFieldValues = d.fields ?? {};
      this.#savedSigs        = d.sigs ?? {};
    } else {
      this.#savedFieldValues = {};
      this.#savedSigs        = {};
    }
  }

  #persistDraft() {
    const G = this.gameStore.getState();
    const L = this.localStore.getState();
    if (G.round < 0 || !L.team) return;
    storage.set(DRAFT_KEY, {
      team:   L.team,
      round:  G.round,
      fields: this.#savedFieldValues,
      sigs:   this.#savedSigs,
    });
  }

  /** Debounced — signature dataURLs make the draft a few KB, so don't
   *  serialise on every keystroke. */
  #persistDraftSoon() {
    clearTimeout(this.#draftTimer);
    this.#draftTimer = setTimeout(() => this.#persistDraft(), 400);
  }

  template() {
    const G = this.gameStore.getState();
    const L = this.localStore.getState();
    if (!L.team) return `
      <div class="card" style="text-align:center;padding:32px 16px">
        <p class="muted" style="font-size:13px;margin-bottom:12px">No team is set up on this device yet.</p>
        <button class="btn btn--primary btn--sm" id="team-setup-btn">Set up team</button>
      </div>`;

    const teamKey = fk(L.team);
    const pts     = teamTotal(G.scores, teamKey);
    const { max, complete } = scoredProgress(G.scores, teamKey, ROUNDS);
    const lic     = licenceTier(pts, max, complete);
    const awardedBadges = BADGES.filter(b => G.badges[teamKey]?.[b.id]);

    const header = `
      <div class="card card--row" style="padding:8px 12px;margin-bottom:10px">
        <span class="team-name" style="font-size:13px;font-weight:700;color:var(--surface-navy)">${escHtml(teamLabel(L.team, L.displayName))}</span>
        ${pts > 0 ? `<span class="licence-badge ${lic.cls}" style="margin-left:auto" title="${pts} / ${max}">${lic.label}</span>` : '<span style="margin-left:auto"></span>'}
        ${pts > 0 ? `<span style="font-weight:700;font-size:14px;color:var(--surface-navy)">${pts} pts</span>` : ''}
        ${pts > 0 ? `<span class="muted" style="font-size:10px;width:100%;text-align:right">${complete ? 'Final result — simulation complete' : 'Your standing so far — updates as each round is scored'}</span>` : ''}
      </div>
    `;

    const badgeCard = awardedBadges.length ? `
      <div class="card" style="margin-bottom:10px">
        <div class="card__heading">Badges earned</div>
        <div>${awardedBadges.map(b => `<span class="badge-pill ${b.cls}">${b.icon} ${escHtml(b.label)}</span>`).join('')}</div>
      </div>
    ` : '';

    const body = this.#renderBody(G, L);
    return header + badgeCard + body;
  }

  #renderBody(G, L) {
    const teamKey = fk(L.team);
    const noActiveRound  = G.round < 0;
    const roundEnded     = G.round >= 0 && !G.active && !G.subs[`${teamKey}_R${G.round}`] && !G.returned[`${teamKey}_R${G.round}`];
    const submitted      = !!G.subs[`${teamKey}_R${G.round}`];
    const returned       = !!G.returned[`${teamKey}_R${G.round}`];

    if (noActiveRound) return this.#renderWaiting('waiting');
    if (roundEnded)    return this.#renderWaiting('ended');
    if (submitted)     return this.#renderDone();
    return this.#renderActive(G, L);
  }

  #renderWaiting(state) {
    return state === 'ended'
      ? `<div class="card" style="text-align:center;padding:32px 16px">
           <div style="font-size:24px;margin-bottom:8px" aria-hidden="true">⏸</div>
           <p style="font-size:14px;font-weight:600;color:var(--surface-navy)">Round ended</p>
           <p class="muted" style="font-size:12px;margin-top:4px">Wait for the next round.</p>
         </div>`
      : `<div class="card" style="text-align:center;padding:32px 16px" role="status" aria-live="polite">
           <div style="font-size:28px;margin-bottom:8px" aria-hidden="true">⏳</div>
           <p style="font-size:14px;font-weight:600;color:var(--surface-navy)">Waiting for the facilitator</p>
           <p class="muted" style="font-size:12px;margin-top:4px">Your team is registered.</p>
         </div>`;
  }

  #renderDone() {
    return `<div class="card" style="text-align:center;padding:24px" role="status" aria-live="polite">
      <div style="font-size:28px;margin-bottom:6px" aria-hidden="true">✅</div>
      <p style="font-size:14px;font-weight:700;color:var(--color-success)">Submission received</p>
      <p class="muted" style="font-size:12px;margin-top:4px">Results appear on the Licence Board after scoring.</p>
    </div>`;
  }

  #renderActive(G, L) {
    const R       = ROUNDS[G.round];
    const teamKey = fk(L.team);
    const returned = !!G.returned[`${teamKey}_R${G.round}`];

    const timerCls = '';
    return `
      <div class="round-header">
        <div class="round-header__eyebrow">${escHtml(R.eyebrow)}</div>
        <h2>${escHtml(R.name)}</h2>
        <p>${escHtml(R.sit)}</p>
      </div>

      <div class="timer-bar" role="timer" aria-label="Round countdown">
        <div>
          <div class="timer-bar__label">Time remaining</div>
          <div class="timer-display" id="t-timer" aria-live="off">--:--</div>
        </div>
        <div class="timer-track"><div class="timer-fill" id="t-tfill" style="width:100%"></div></div>
      </div>

      ${G.bomb ? `
        <div class="bomb-banner bomb-banner--active" role="alert" aria-live="assertive">
          <span class="bomb-banner__icon" aria-hidden="true">⚡</span>
          <div>
            <div class="bomb-banner__label">Bomb Drop — respond now</div>
            <div class="bomb-banner__text">${escHtml(R.bomb)}</div>
          </div>
        </div>` : ''}

      <div class="card">
        <div class="card__heading">What your team must submit</div>
        <div style="font-size:12px;line-height:1.7">${R.brief}</div>
      </div>

      <div class="card" id="t-form">
        <div class="card__heading">Submission — Round ${G.round + 1}</div>
        ${returned ? `<div class="returned-banner" role="alert">↩ <strong>Your submission was returned by the facilitator.</strong> Review your answers, make changes, and resubmit.</div>` : ''}
        ${this.#renderFields(R, G)}
        ${this.#renderBombSection(R, G)}
        ${this.#renderSteward(R)}
        ${this.#renderSignatures(L)}
        <div style="margin-top:12px;display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <button class="btn btn--primary" id="t-subBtn" disabled aria-describedby="t-subStatus">Submit card</button>
          <span id="t-subStatus" class="muted" style="font-size:11px" aria-live="polite">Complete all fields to submit</span>
        </div>
      </div>
    `;
  }

  #renderFields(R, G) {
    let extra = '';

    if (R.priorities) {
      extra = `<div style="overflow-x:auto;margin-bottom:10px">
        <table class="priority-table" role="table" aria-label="Priority options">
          <thead><tr><th scope="col">#</th><th scope="col">Priority</th><th scope="col">The Tension</th></tr></thead>
          <tbody>
            ${R.priorities.map(p => {
              const tag = p.tag === 'short'
                ? '<span class="priority-tag priority-tag--short">SHORT-TERM REACTIVE</span> '
                : p.tag === 'long'
                  ? '<span class="priority-tag priority-tag--long">LONG-TERM STEWARDSHIP</span> '
                  : '';
              return `<tr>
                <td style="font-weight:700">${p.n}</td>
                <td>${tag}${escHtml(p.t)}</td>
                <td class="muted">${escHtml(p.v)}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table></div>`;
    }

    if (R.options) {
      extra = `<div class="investment-grid" role="group" aria-label="Investment options">
        ${R.options.map(o => `
          <div class="investment-option" id="io-${o.id}" role="button" tabindex="0"
               aria-pressed="false" data-option="${o.id}">
            <h4>${escHtml(o.t)}</h4>
            <p>${escHtml(o.d)}</p>
            <div class="investment-option__risk">${escHtml(o.r)}</div>
          </div>`).join('')}
      </div>`;
    }

    const fieldHtml = R.fields.map(f => `
      <div class="field">
        <label for="ff_${f.id}">${escHtml(f.label)} <span class="field__required" aria-label="required">*</span></label>
        <textarea id="ff_${f.id}" name="${f.id}" aria-required="true"
          aria-describedby="hint_${f.id}">${escHtml(this.#savedFieldValues[f.id] ?? '')}</textarea>
        <div class="field__hint" id="hint_${f.id}">${escHtml(f.hint)}</div>
      </div>`).join('');

    return extra + fieldHtml;
  }

  #renderBombSection(R, G) {
    return `<div id="t-bombSection" class="bomb-wrap"${G.bomb ? '' : ' hidden'}>
      <div class="bomb-wrap__label">⚡ Bomb Drop Response</div>
      <div class="field">
        <label for="ff_bomb">${escHtml(R.bombField.label)} <span class="field__required" aria-label="required">*</span></label>
        <textarea id="ff_bomb" name="bomb" aria-required="true"
          aria-describedby="hint_bomb" style="min-height:72px">${escHtml(this.#savedFieldValues.bomb ?? '')}</textarea>
        <div class="field__hint" id="hint_bomb">${escHtml(R.bombField.hint)}</div>
      </div>
    </div>`;
  }

  #renderSteward(R) {
    return `<div class="steward">
      <h4>Steward Check — complete before signing</h4>
      ${R.steward.map((q, i) => `
        <div class="field">
          <label for="ff_sc${i}">${i + 1}. ${escHtml(q)} <span class="field__required" aria-label="required">*</span></label>
          <textarea id="ff_sc${i}" name="sc${i}" aria-required="true"
            style="min-height:50px">${escHtml(this.#savedFieldValues[`sc${i}`] ?? '')}</textarea>
        </div>`).join('')}
    </div>`;
  }

  #renderSignatures(L) {
    const sigs = L.members.map((name, i) => `
      <div class="signature-item" id="sig${i}">
        <div class="signature-header">
          <span class="signature-name">${escHtml(name)}</span>
          <button class="signature-clear" type="button" data-sig="${i}" aria-label="Clear ${escHtml(name)}'s signature">Clear</button>
        </div>
        <div style="position:relative">
          <canvas class="signature-canvas" id="scv${i}" height="56"
            role="img" aria-label="${escHtml(name)}'s signature area"></canvas>
          <span class="signature-placeholder" id="scp${i}" aria-hidden="true">Sign here</span>
        </div>
      </div>`).join('');

    return `<div class="signatures">
      <div style="font-size:11px;font-weight:700;color:var(--color-text);margin-bottom:8px">
        All team members must sign before submitting
      </div>
      <div class="signature-grid" id="t-sigs">${sigs}</div>
    </div>`;
  }

  afterRender() {
    const G = this.gameStore.getState();
    if (G.round < 0) return;
    const L = this.localStore.getState();
    if (!G.active && !G.returned[`${fk(L.team)}_R${G.round}`]) return;
    if (G.subs[`${fk(L.team)}_R${G.round}`]) return;

    requestAnimationFrame(() => {
      L.members.forEach((_, i) => this.#initPad(G.round, i));
      this.#checkReady();
    });
  }

  #initPad(round, i) {
    const key    = `${round}_${i}`;
    const canvas = document.getElementById(`scv${i}`);
    if (!canvas || this.#pads[key]) return;
    const pad = new SignaturePad(canvas);
    this.#pads[key] = pad;
    if (this.#savedSigs[i]) pad.restore(this.#savedSigs[i]);
    const onSigned = () => {
      document.getElementById(`scp${i}`)?.style.setProperty('display', 'none');
      document.getElementById(`sig${i}`)?.classList.add('signature-item--signed');
      this.#savedSigs[i] = pad.dataURL;
      this.#persistDraftSoon();
      this.#checkReady();
    };
    const onCleared = () => {
      document.getElementById(`scp${i}`)?.style.removeProperty('display');
      document.getElementById(`sig${i}`)?.classList.remove('signature-item--signed');
      delete this.#savedSigs[i];
      this.#persistDraftSoon();
      this.#checkReady();
    };
    canvas.addEventListener('sig:signed',  onSigned);
    canvas.addEventListener('sig:cleared', onCleared);
    this.addCleanup(() => {
      canvas.removeEventListener('sig:signed',  onSigned);
      canvas.removeEventListener('sig:cleared', onCleared);
      pad.destroy();
    });
  }

  bindEvents() {
    const setupBtn = this.$('#team-setup-btn');
    if (setupBtn) {
      const handler = () => this.emit('team:setup');
      setupBtn.addEventListener('click', handler);
      this.addCleanup(() => setupBtn.removeEventListener('click', handler));
    }

    const subBtn = this.$('#t-subBtn');
    if (subBtn) {
      const handler = () => this.#doSubmit();
      subBtn.addEventListener('click', handler);
      this.addCleanup(() => subBtn.removeEventListener('click', handler));
    }

    // Investment option click/keyboard
    this.$$('.investment-option').forEach(opt => {
      const select = () => this.#selectOption(opt.dataset.option);
      opt.addEventListener('click', select);
      opt.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } });
      this.addCleanup(() => opt.removeEventListener('click', select));
    });

    // Live field validation
    this.$$('textarea').forEach(ta => {
      const handler = () => {
        this.#savedFieldValues[ta.name] = ta.value;
        this.#persistDraftSoon();
        this.#checkReady();
      };
      ta.addEventListener('input', handler);
      this.addCleanup(() => ta.removeEventListener('input', handler));
    });

    // Sig clear buttons
    this.$$('.signature-clear').forEach(btn => {
      const handler = () => {
        const i   = parseInt(btn.dataset.sig, 10);
        const G   = this.gameStore.getState();
        const pad = this.#pads[`${G.round}_${i}`];
        pad?.clear();
      };
      btn.addEventListener('click', handler);
      this.addCleanup(() => btn.removeEventListener('click', handler));
    });
  }

  #selectOption(id) {
    this.$$('.investment-option').forEach(el => {
      const sel = el.dataset.option === id;
      el.classList.toggle('investment-option--selected', sel);
      el.setAttribute('aria-pressed', sel);
    });
    const field = document.getElementById('ff_option');
    if (field) {
      field.value = `Option ${id}`;
      this.#savedFieldValues.option = `Option ${id}`;
      this.#persistDraftSoon();
      this.#checkReady();
    }
  }

  #checkReady() {
    const G = this.gameStore.getState();
    const L = this.localStore.getState();
    if (G.round < 0) return;

    const R             = ROUNDS[G.round];
    const fieldsOk      = R.fields.every(f => document.getElementById(`ff_${f.id}`)?.value.trim());
    const stewardOk     = R.steward.every((_, i) => document.getElementById(`ff_sc${i}`)?.value.trim());
    const bombAnswered  = G.bomb && !!document.getElementById('ff_bomb')?.value.trim();
    const signedCount   = L.members.filter((_, i) => this.#pads[`${G.round}_${i}`]?.hasSigned).length;
    const sigsNeeded    = Math.min(4, L.members.length);
    const sigsOk        = signedCount >= sigsNeeded;
    const ready         = fieldsOk && stewardOk && bombAnswered && sigsOk;

    const btn    = document.getElementById('t-subBtn');
    const status = document.getElementById('t-subStatus');
    if (btn)    btn.disabled = !ready;
    if (status) {
      if (!G.bomb)         status.textContent = '⚡ Waiting for bomb drop — cannot submit yet';
      else if (!fieldsOk || !stewardOk) status.textContent = 'Complete all fields and Steward Check';
      else if (!bombAnswered)           status.textContent = 'Answer the bomb drop question';
      else if (!sigsOk)                 status.textContent = `${signedCount} of ${sigsNeeded} signatures needed`;
      else                              status.textContent = '';
    }
  }

  async #doSubmit() {
    const G = this.gameStore.getState();
    const L = this.localStore.getState();
    if (G.round < 0 || !L.team) return;

    const R    = ROUNDS[G.round];
    const key  = `${fk(L.team)}_R${G.round}`;
    const data = { team: L.team, round: G.round, ts: Date.now(), members: L.members };

    R.fields.forEach(f => { data[f.id] = document.getElementById(`ff_${f.id}`)?.value ?? ''; });
    data.bomb = document.getElementById('ff_bomb')?.value ?? '';
    R.steward.forEach((_, i) => { data[`sc${i}`] = document.getElementById(`ff_sc${i}`)?.value ?? ''; });
    data.sigs = L.members.map((name, i) => {
      const pad = this.#pads[`${G.round}_${i}`];
      return { name, img: pad?.dataURL ?? '' };
    });

    const btn = document.getElementById('t-subBtn');
    if (btn) btn.disabled = true;

    try {
      await this.firebase.removeGame(`returned/${key}`);
      await this.firebase.writeGame(`subs/${key}`, data);
    } catch (e) {
      console.error('[TeamView] submit failed:', e);
      if (btn) btn.disabled = false;
      this.emit('app:error', { message: 'Submission failed. Please try again.' });
    }
  }

  #destroyPads() {
    Object.values(this.#pads).forEach(p => p.destroy());
    this.#pads = {};
  }

  destroy() {
    clearTimeout(this.#draftTimer);
    this.#destroyPads();
    super.destroy();
  }
}
